/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { Constants } from '@shared/models/constants.model';
import { EventsService } from './events.service';
import { HttpHelpersService } from './http-helpers.service';
import { videoStates } from '@video/video-state.enum';
import { StorageService } from './storage.service';

@Injectable({ providedIn: 'root' })
export class VideoService {
  private readonly CLIPPING_TUTORIAL_KEY = 'clipping_tutorial';

  constructor(
    private eventsService: EventsService,
    private storageService: StorageService,
    private httpClient: HttpClient
  ) {}

  getSportName(videoData): string {
    if (videoData.event && videoData.event.event && videoData.event.event.sport) {
      return videoData.event.event.sport.name;
    }
    return videoData.event && videoData.event.sport ? videoData.event.sport.name : videoData.sportName;
  }

  getIsFreeContent(video) {
    let metadata = video.metadata;

    if (!video.type || (video.type && video.type !== Constants.ASSET_TYPE.FULL_GAME_VIDEO)) {
      return true;
    }

    if (metadata && typeof metadata === 'string') {
      metadata = JSON.parse(metadata);
    }

    // if content is free
    if (
      metadata &&
      (metadata[Constants.FREE_CONTENT_TYPES.CHAMPIONSHIP_GAME] || metadata[Constants.FREE_CONTENT_TYPES.ALL_STAR_GAME])
    ) {
      return true;
    }

    return false;
  }

  hasAccessToVideo(video, user, event, gameTeams = [], ignoreFreeContent?): Observable<any> {
    // if video is free content
    if (!ignoreFreeContent && this.getIsFreeContent(video)) {
      return of(videoStates.WATCH);
    }

    const hasFullAccess =
      user.plan === Constants.PLAN_TYPE.ALL_EVENTS ||
      user.type === Constants.USER_TYPE.HEAD_COACH ||
      user.type === Constants.USER_TYPE.ADMIN;

    if (hasFullAccess) {
      return of(videoStates.WATCH);
    }

    return combineLatest([
      this.eventsService.hasAccessToEvent(user, event),
      this.eventsService.getUserPurchases(),
    ]).pipe(
      map(([hasEventAccess, userPurchases]) => {
        if (!hasEventAccess) {
          return videoStates.FORBIDDEN;
        }
        /* check full access to channel events */
        const fullChannelAccessPurchase = userPurchases.find(
          (p) =>
            p.type === Constants.PURCHASE_TYPE.CHANNEL_GF &&
            event.channel &&
            p.channelId &&
            p.channelId === event.channelId &&
            p.metadata &&
            p.metadata.fullAccess
        );
        if (fullChannelAccessPurchase) {
          return videoStates.WATCH;
        }

        /* Check access for selected teams */
        if (gameTeams && gameTeams.length) {
          const eventId = event ? event.id : video.eventId;
          const selectedTeams = this.eventsService.getSelectedTeams(eventId, userPurchases);

          // return true/false if team is in selection
          const selectedTeamAccess =
            Array.isArray(selectedTeams.data) &&
            gameTeams.some((team) => selectedTeams.data.some((selectedTeamId) => team.id === selectedTeamId));
          return selectedTeamAccess ? videoStates.WATCH : videoStates.FORBIDDEN_TEAM;
        }

        return videoStates.WATCH;
      })
    );
  }

  setClippingTutorialViewed(newValue, userId) {
    this.storageService.setLocalStorageItem(`${this.CLIPPING_TUTORIAL_KEY}-${userId}`, newValue ? '1' : '0');
  }

  getClippingTutorialViewed(userId) {
    const value = this.storageService.getLocalStorageItem(`${this.CLIPPING_TUTORIAL_KEY}-${userId}`);
    return value === '1' ? true : false;
  }

  formatVideoTimestamp(timestamp: number): string {
    let time = '';
    const hours = Math.floor(timestamp / 60 / 60);
    const minutes = Math.floor(timestamp / 60) - hours * 60;
    const seconds = timestamp % 60;

    time = minutes.toString().padStart(2, '0') + ':' + seconds.toString().padStart(2, '0');
    if (hours) {
      time = hours.toString().padStart(2, '0') + ':' + time;
    }
    return time;
  }

  getHomepageSectionVideos(section: string, limit = 0, randomizeResults?) {
    const query = {
      metadataField: section,
      randomizeResults,
      isShownOnHomePage: true,
      take: undefined,
    };
    if (limit) {
      query.take = limit;
    }
    return HttpHelpersService.handleGetResponse(
      this.httpClient.post(`${environment.apiHostname}/v1/assets/search`, query)
    );
  }
}
